import {
    useEffect, useMemo, useRef, useContext, useState
} from 'react';
import { useSelector } from 'react-redux';
import { BrandDescription } from '@common/Brandwall/BrandDescription';
import { useConfig } from '@common/Hooks/useConfig';
import { useTranslation } from '@common/Hooks/useTranslation';
import { LeftNavigation } from '@common/LeftNavigation/components/LeftNavigation';
import { ListingBottom } from '@common/Listing/Bottom/containers/ListingBottom';
import {
    BannerCard, LeftSidebarLists, ListingTop, ListingTopMobile, ListingTopSticky
} from '@common/Listing/components/';
import { ListingFaq } from '@common/Listing/components/ListingFaq';
import { RelatedListings } from '@common/Listing/components/RelatedListings';
import { GRID_COL_NUM } from '@common/Listing/constants';
import { ProductListWrap } from '@common/Listing/containers';
import { setNavigationIdValue } from '@common/Listing/helpers/pdp';
import { prepareTrackingDataAttributes } from '@common/Listing/helpers/tracking';
import ListingContext from '@common/Listing/ListingContext';
import {
    listingGridSelector, selectListingPageInfo, selectListingProductsInfo
} from '@common/Listing/selectors/ListingSelector';
import { ListingTopHead } from '@common/Listing/Top/components/ListingTopHead';
import { Pagination } from '@common/Pagination/containers/Pagination';
import { removeSponsoredTrackingData } from '@common/Product/Sponsor/helpers/removeSponsoredTrackingData';
import { SponsoredProductsCard } from '@common/Product/Sponsor/SponsoredProductsCard';
import ProductCard from '@common/ProductCard/containers/ProductCardRedesign';
import { RecentProductsCarousel } from '@common/Recents/containers/RecentProductsCarousel';
import ScrollToTop from '@common/ScrollToTop/components/ScrollToTop';
import { FAVORITES_EVENTS_LABELS, dispatchGaPageviewEvent } from '@common/Tracking';
import { GA4ViewItemListEvent } from '@common/Tracking/GaTracking/GaListing';
import { trackEvent } from '@helpers/analytics';
import { isWebshopMobile, isWebshopDesktop } from '@helpers/env';
import { isFeatureActive } from '@helpers/index';
import { isKindlyActive } from '@helpers/kindly';
import './ListingPage.scss';

function ListingPage({
    descriptionBottom,
    brandDescriptionBottom,
    persistedFiltersGroup,
    filtersProps,
    listingTopHeadProps,
    linkParams,
    isListingGrouped,
    breadcrumbs,
    faqs,
    faqTitleHeadingTag,
    faqQuestionHeadingTag,
    relatedListings
}) {
    const config = useConfig();
    const [isKindlyButtonPresent, setIsKindlyButtonPresent] = useState(false);
    const translate = useTranslation();
    const { gridView, gridViewFromConfig } = useSelector(listingGridSelector);
    const listingState = useSelector(state => state.listing);
    const productsFromStore = useSelector(({ listing }) => listing.products);
    const { hasProducts } = useContext(ListingContext);
    const { currentPage } = useSelector(state => state.pagination);
    const { productsPerPage } = useSelector(selectListingProductsInfo);
    const { productsGroup } = useSelector(selectListingPageInfo);
    const productsFiltered = useMemo(() => {
        if (config.isActiveFeature('PLT-66907-sponsored-products-client-side')) {
            return productsFromStore.filter(product => !product.data.sponsored_product_tracking);
        }

        return productsFromStore;
    }, [config, productsFromStore]);
    const [products, setProducts] = useState(productsFiltered);

    const initialMountRef = useRef(true);
    const listingPageRef = useRef(null);
    const isInitialGA4EventDone = useRef(false);

    const isStickyFiltersFeatureActive = isFeatureActive('PLT-60847-sticky-filters', config);
    const isListingFaqFeatureActive = isFeatureActive('PLT-51255-listing-faq', config);
    const isRelatedListingsFeatureActive = isFeatureActive('PLT-48240-related-listings', config);

    const cardLabels = {
        comingSoon: translate('brandaz.coming-soon.label'),
        quantity: translate('shopcart.quantity.label'),
        outOfStock: translate('out_of_stock')
    };

    const {
        navigationId,
        baseNavigationId,
        overlayActive,
        trackingName: realListValue,
        availableImageDimensions,
        gridImageSizes
    } = listingState;

    const itemsParameters = {
        item_list_id: navigationId,
        item_list_name: realListValue,
        item_list_type: productsGroup
    };

    useEffect(() => {
        if (initialMountRef.current && productsFromStore.length !== productsFiltered.length) {
            setProducts(productsFromStore);
        }
    }, [productsFiltered, productsFromStore]);

    useEffect(() => {
        if (!initialMountRef.current) {
            setProducts(productsFromStore);
        }
    }, [productsFromStore]);

    useEffect(() => {
        dispatchGaPageviewEvent({
            noResults: !hasProducts
        });

        if (!hasProducts) {
            trackEvent('open', 'emptyProductPage', window.location.href);
        }
    }, [hasProducts]);

    useEffect(() => {
        if (hasProducts && !isInitialGA4EventDone.current) {
            GA4ViewItemListEvent(navigationId, baseNavigationId, productsGroup, config);
            isInitialGA4EventDone.current = true;
        }
    }, [products, hasProducts, config, productsGroup, navigationId, baseNavigationId]);

    useEffect(() => {
        if (initialMountRef.current) {
            initialMountRef.current = false;
        }
    }, []);

    useEffect(() => {
        setIsKindlyButtonPresent(isKindlyActive(config));
    }, [config]);

    const scrollListingIntoView = () => {
        listingPageRef.current.scrollIntoView();
    };

    const selectedGrid = gridViewFromConfig ?? gridView;
    const shouldShowBrandDescriptionBottom = !!brandDescriptionBottom?.text && currentPage === 1;
    const shouldShowListingFaqs = faqs.length > 0;
    const shouldShowRelatedListings = relatedListings?.length > 0;

    const listing = useMemo(() => products.map((element, index) => {
        const isProduct = element.type === 'product';

        if (isProduct) {
            const { data: product } = element;
            const { bmp_listing: bmpListingTags } = product.tags ?? {};
            const { path } = linkParams ?? {};
            const bmpData = {
                totalProducts: products.length,
                productsPerPage,
                currentPage: Number(currentPage),
                itemIndex: index,
                group: productsGroup,
                productsPerRow: GRID_COL_NUM[selectedGrid] ?? 2,
                navigationId,
                ...(bmpListingTags && { tagIds: bmpListingTags }),
                ...(path && { path })
            };
            const firstVariantId = product.variants[0].id;

            if (product.sponsored_product_tracking) {
                return (
                    <SponsoredProductsCard
                        key={firstVariantId}
                        product={product}
                        trackingValues={{
                            dataAttributes: { 'data-product-id': product.id },
                            list: realListValue,
                            sponsored: {
                                currentPage: Number(currentPage),
                                productIndex: index,
                                productsGroup,
                                productsPerPage,
                                productsPerRow: GRID_COL_NUM[selectedGrid] ?? 2,
                                totalProducts: products.length
                            },
                            itemsParameters,
                            interactionOrigin: productsGroup
                        }}
                    />
                );
            }

            return (
                <ProductCard
                    key={`${firstVariantId}${index}`} // eslint-disable-line react/no-array-index-key
                    allowVideo
                    hasToShowFavoriteButton
                    hasToTrackImpressions
                    lazyLoad
                    availableImageDimensions={availableImageDimensions}
                    breadcrumbs={breadcrumbs}
                    cardStrings={cardLabels}
                    className="product-listing__card"
                    hasHoverEffect={isListingGrouped}
                    imageSizes={gridImageSizes[selectedGrid] ?? gridImageSizes.small}
                    product={product}
                    trackingValues={{
                        favorites: FAVORITES_EVENTS_LABELS.listing,
                        list: realListValue,
                        bmp: bmpData,
                        dataAttributes: prepareTrackingDataAttributes({
                            product, navigationId, linkParams, index
                        }),
                        itemsParameters,
                        interactionOrigin: productsGroup
                    }}
                    withAnimation={index === 0}
                    onClick={removeSponsoredTrackingData}
                    onLinkClick={(event) => {
                        setNavigationIdValue(event, navigationId);
                    }}
                    onLinkContextMenu={(event) => {
                        setNavigationIdValue(event, navigationId);
                        removeSponsoredTrackingData();
                    }}
                />
            );
        }

        const bannerContent = element?.content ?? element?.data;

        return (
            <BannerCard
                key={element.id}
                banner={bannerContent}
                className="product-card"
            />
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [products]);

    return (
        <div
            ref={listingPageRef}
            className="product-listing__wrapper"
        >
            <div className="product-listing__wrapper-column product-listing__wrapper-column--left-sidebar">
                {isWebshopDesktop(config) && (
                    <>
                        <LeftNavigation persistedFiltersGroup={persistedFiltersGroup} />
                        <LeftSidebarLists />
                    </>
                )}
            </div>
            <div className="product-listing__wrapper-column product-listing__wrapper-column--main">
                {!!listingTopHeadProps && (
                    <ListingTopHead
                        {...listingTopHeadProps}
                        categoryName={filtersProps.categoryName}
                    />
                )}
                {isWebshopMobile(config) && (
                    <ListingTopMobile
                        filtersProps={filtersProps}
                        onModalClose={scrollListingIntoView}
                    />
                )}
                {/* TODO: remove once A/B test is done */}
                {isWebshopDesktop(config) && (
                    isStickyFiltersFeatureActive
                        ? <ListingTopSticky {...filtersProps} />
                        : <ListingTop {...filtersProps} />
                )}
                <ProductListWrap
                    isOverlayActive={overlayActive}
                    numberOfItems={products.length}
                >
                    {listing}
                </ProductListWrap>
                <Pagination onChange={scrollListingIntoView} />
                {!isKindlyButtonPresent && <ScrollToTop ref={listingPageRef} />}
                {(descriptionBottom || brandDescriptionBottom) && (
                    <div className="product-listing__bottom-description">
                        { descriptionBottom && <ListingBottom {...descriptionBottom} /> }
                        { shouldShowBrandDescriptionBottom && <BrandDescription {...brandDescriptionBottom} /> }
                    </div>
                )}
                {
                    (isListingFaqFeatureActive && shouldShowListingFaqs) && (
                        <ListingFaq
                            faqQuestionHeadingTag={faqQuestionHeadingTag}
                            faqs={faqs}
                            faqTitleHeadingTag={faqTitleHeadingTag}
                        />
                    )
                }
                {
                    (isRelatedListingsFeatureActive && shouldShowRelatedListings) && (
                        <RelatedListings relatedListings={relatedListings} />
                    )
                }
            </div>
            <div className="product-listing__wrapper-column product-listing__wrapper-column--mobile-underlisting">
                {isWebshopMobile(config) && (
                    <RecentProductsCarousel imageDimension={gridImageSizes.small.dimension} />
                )}
            </div>
        </div>
    );
}

export default ListingPage;
